import { FlexColumnSpaced } from '@fox/layout/src/flex';
import * as React from 'react';
import { PropsWithChildren } from 'react';
import { Button } from '../button';
import { MuiAlert, MuiAlertTitle } from '../mui-alert';

export type ErrorBoundaryProps = {
	customActions?: React.ReactNode;
	onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
	renderError?: (error: Error, errorInfo: React.ErrorInfo, clearError: () => void) => React.ReactNode;
};

export interface ErrorBoundaryState {
	hasError: boolean;
	error?: Error;
	info?: React.ErrorInfo;
}

export interface ExceptionProps {
	className?: string;
	type?: '403' | '404' | '500' | 'JsError';
	title?: React.ReactNode;
	desc?: React.ReactNode;
	img?: string;
	actions?: React.ReactNode;
	style?: React.CSSProperties;
	stack?: string;
	componentStack?: string;
	clearError?: () => void;
}

interface ErrorConfig {
	img: React.ReactNode;
	title: string;
	desc?: React.ReactNode;
}

const typeConfig: { [key: string]: ErrorConfig } = {
	'403': {
		img: '403', //logo_403,
		title: 'Forbidden',
		desc: "Sorry, you don't have access to this page"
	},
	'404': {
		img: '404', //logo_404,
		title: 'Not Found',
		desc: 'Sorry, the page you are looking for does not exist'
	},
	'500': {
		img: '500', //logo_500,
		title: 'Error',
		desc: 'An internal server error occurred'
	},
	JsError: {
		img: '500', // logo_500,
		title: 'Script Error',
		desc: 'A scripting error occurred'
	}
};

export const Exception = ({ className, clearError, type, title, desc, img, actions, ...rest }: ExceptionProps) => {
	let errorConf;
	const pageType = type;
	if (!pageType || !typeConfig[pageType]) {
		errorConf = typeConfig['404'];
	} else {
		errorConf = typeConfig[pageType];
	}

	return (
		<div>
			<div style={{ backgroundImage: `url(${img || errorConf.img})` }} />

			<div>
				<h1>{title || errorConf.title}</h1>
				<div>{desc || errorConf.desc}</div>
				{rest.stack && (
					<div>
						<p>Stack Trace</p>
						<div>{rest.stack}</div>
					</div>
				)}
				{rest.componentStack && (
					<div>
						<p>Component Stack Trace</p>
						<div>{rest.componentStack}</div>
					</div>
				)}
				<div>
					{actions ? (
						actions
					) : clearError ? (
						<Button variant="primary" color="primary" onClick={() => clearError()}>
							Clear the error
						</Button>
					) : null}
				</div>
			</div>
		</div>
	);
};

export function ErrorBoundaryWithReset({ children }: PropsWithChildren) {
	return (
		<ErrorBoundary
			renderError={(error, info, clearError) => (
				<MuiAlert
					severity="error"
					action={
						<Button variant="secondary" color="inherit" onClick={clearError}>
							Reset
						</Button>
					}
				>
					<FlexColumnSpaced>
						<MuiAlertTitle>Something went wrong</MuiAlertTitle>
						{error.message}
					</FlexColumnSpaced>
				</MuiAlert>
			)}
		>
			{children}
		</ErrorBoundary>
	);
}

export class ErrorBoundary extends React.Component<PropsWithChildren<ErrorBoundaryProps>, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = {
			hasError: false
		};
	}

	componentDidCatch(error: Error, info: React.ErrorInfo) {
		// Display fallback UI
		this.setState({ hasError: true, error: error, info: info });
		// You can also log the error to an error reporting service
		//logErrorToMyService(error, info);
		this.props.onError?.(error, info);
	}

	render() {
		const { hasError, error, info } = this.state;
		if (hasError) {
			return (
				<>
					{this.props.renderError ? (
						this.props.renderError(error, info, () => this.setState({ hasError: false, error: undefined, info: undefined }))
					) : (
						<Exception
							type="JsError"
							stack={error.stack}
							componentStack={info.componentStack}
							clearError={() => this.setState({ hasError: false, error: undefined, info: undefined })}
							actions={this.props.customActions}
						/>
					)}
				</>
			);
		} else {
			return this.props.children;
		}
	}
}
